import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { RawEditorSettings } from 'tinymce';
import Input from '../../forms/Input';
import Button from '../../forms/Buttom';

import { ContentPage } from './styles';
import { IEditResource, IPages } from '.';
import { useToast } from '../../../hooks/toast';
import { escapeHtml } from '../../../utils/escapeHtml';
import { normalizePodcastEmbedUrl } from '../../../utils/embedUrl';
import Upload from '../../forms/Upload';
import { uploadFiles } from '../../../services/api';
import { useAuth } from '../../../hooks/auth';

interface IProps {
  handlePage(page: IPages): void;
  propsEditor: RawEditorSettings;
  setIsOpen: () => void;
  editResource: IEditResource;
}

const FormPodcast: React.FC<IProps> = ({
  handlePage,
  propsEditor,
  setIsOpen,
  editResource,
}) => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const formRef = useRef<FormHandles>(null);
  const [uploading, setUploading] = useState(false);

  const isEdit = useMemo(() => {
    return Object.keys(editResource).length > 0;
  }, [editResource]);

  const handleUpload = useCallback(
    async (files: File[]) => {
      if (!user.provider?.id) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível identificar o provedor.',
          type: 'error',
        });
        return;
      }

      try {
        setUploading(true);
        const formData = new FormData();
        formData.append('podcasts', files[0]);
        const response = await uploadFiles({
          formData,
          provider_id: user.provider.id,
          type: 'podcasts',
        });
        const podcastUrl = response?.data[0]?.url;
        formRef.current?.setFieldValue('link', podcastUrl || '');
        addToast({
          title: 'Sucesso!',
          description: 'Podcast enviado com sucesso.',
          type: 'success',
        });
      } catch (error) {
        addToast({
          title: 'Ops ...',
          description: 'Não foi possível enviar o podcast.',
          type: 'error',
        });
      } finally {
        setUploading(false);
      }
    },
    [addToast, user.provider],
  );

  const handleSubmit = useCallback(
    async data => {
      const title = data.title?.trim();
      if (!title || !data.link?.trim()) {
        addToast({
          title: 'Ops ...',
          description: 'Informe o título e o link do podcast',
          type: 'error',
        });
        return;
      }

      const linkPodcast = normalizePodcastEmbedUrl(data.link);
      if (!linkPodcast) {
        addToast({
          title: 'Ops ...',
          description: 'Informe um link de podcast válido',
          type: 'error',
        });
        return;
      }

      if (isEdit) {
        editResource.element.setAttribute('data-resource', linkPodcast);
        editResource.element.textContent = title;
        propsEditor.insertContent(propsEditor.selection.getContent());
      } else {
        propsEditor.insertContent(
          `<div class="resource" data-interaction="true" data-type="media" data-subtype="podcast" data-resource="${escapeHtml(
            linkPodcast,
          )}">
              ${escapeHtml(title)}
            </div>\n
            <p>&nbsp;</p>`,
        );
      }

      propsEditor.focus();
      setIsOpen();
    },
    [propsEditor, setIsOpen, addToast, isEdit, editResource.element],
  );
  return (
    <Form
      ref={formRef}
      onSubmit={handleSubmit}
      initialData={{
        title: editResource.element?.textContent?.trim(),
        link: editResource.resource,
      }}
    >
      <ContentPage>
        {!isEdit && (
          <button type="button" onClick={() => handlePage('main')}>
            Voltar
          </button>
        )}
        <div>
          <h2>Podcast</h2>
          <Input
            label="Título do podcast"
            name="title"
            type="text"
            placeholder="Ex.: Crescimento na adolescência"
          />
          <Input
            label="Inserir um podcast"
            name="link"
            type="text"
            placeholder='<iframe src="https://w.soundcloud.com/player/... />'
          />
          <p>Ou envie o arquivo do podcast:</p>
          <Upload
            onUpload={handleUpload}
            loading={uploading}
            maxSizeMb={200}
            formts={[
              { extension: '.mp3', types: 'audio/mpeg' },
              { extension: '.m4a', types: 'audio/mp4' },
              { extension: '.aac', types: 'audio/aac' },
              { extension: '.ogg', types: 'audio/ogg' },
              { extension: '.wav', types: 'audio/wav' },
              { extension: '.webm', types: 'audio/webm' },
            ]}
          />
          <Input hidden label="" name="type" defaultValue="podcast" />
          <Button label="Inserir" type="submit" isLoading={uploading} />
        </div>
      </ContentPage>
    </Form>
  );
};

export default FormPodcast;
